import { useState, useEffect, useRef } from "react";
import { Input } from "@/config/antd";
import { parse, format, isValid } from "date-fns";
import PropTypes from "prop-types";

const TimeRangeInput = ({ fromTime, toTime, onFromChange, onToChange }) => {
  const [fromPeriod, setFromPeriod] = useState("AM");
  const [toPeriod, setToPeriod] = useState("AM");
  const [fromTimeInput, setFromTimeInput] = useState("");
  const [toTimeInput, setToTimeInput] = useState("");
  const [fromError, setFromError] = useState("");
  const [toError, setToError] = useState("");
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  // Initialize time inputs with validation
  useEffect(() => {
    try {
      if (fromTime && /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(fromTime)) {
        const date = parse(fromTime, "HH:mm", new Date());
        if (isValid(date)) {
          setFromTimeInput(format(date, "hh:mm"));
          setFromPeriod(format(date, "a"));
        }
      }

      if (toTime && /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(toTime)) {
        const date = parse(toTime, "HH:mm", new Date());
        if (isValid(date)) {
          setToTimeInput(format(date, "hh:mm"));
          setToPeriod(format(date, "a"));
        }
      }
    } catch (err) {
      console.warn("Invalid time format received:", {
        fromTime,
        toTime,
        error: err.message,
      });
      // Reset to default values if parsing fails
      setFromTimeInput("");
      setToTimeInput("");
      setFromPeriod("AM");
      setToPeriod("AM");
    }
  }, [fromTime, toTime]);

  const validateTimeFormat = (time) => {
    const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!regex.test(time)) return false;

    const [hours, minutes] = time.split(":").map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const convertTo24Hour = (time, period) => {
    if (!time) return "";

    try {
      const [hours, minutes] = time.split(":").map(Number);
      let hour24 = hours;

      if (period === "PM" && hours !== 12) {
        hour24 = hours + 12;
      } else if (period === "AM" && hours === 12) {
        hour24 = 0;
      }

      return `${hour24.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  const handleTimeInput = (value, type) => {
    const regex = /^[0-9:]*$/;
    if (!regex.test(value)) return;

    const inputRef = type === "from" ? fromInputRef : toInputRef;
    const prevValue = type === "from" ? fromTimeInput : toTimeInput;

    // Handle deletion - if we're deleting and removing the colon, clear and start over
    if (
      prevValue.includes(":") &&
      !value.includes(":") &&
      prevValue.length > value.length
    ) {
      if (type === "from") {
        setFromTimeInput("");
        setFromError("");
      } else {
        setToTimeInput("");
        setToError("");
      }
      return;
    }

    // Auto-format logic for hours entry
    if (!value.includes(":")) {
      // For single digit (1-9)
      if (value.length === 1 && parseInt(value) > 1) {
        const formattedValue = `0${value}:`;
        if (type === "from") {
          setFromTimeInput(formattedValue);
          setFromError("");
        } else {
          setToTimeInput(formattedValue);
          setToError("");
        }
        // Set cursor position after the colon
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(3, 3);
          }
        }, 0);
        return;
      }

      // For double digits (10-12)
      if (value.length === 2) {
        const formattedValue = `${value}:`;
        if (type === "from") {
          setFromTimeInput(formattedValue);
          setFromError("");
        } else {
          setToTimeInput(formattedValue);
          setToError("");
        }
        // Set cursor position after the colon
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(3, 3);
          }
        }, 0);
        return;
      }
    }

    if (type === "from") {
      setFromTimeInput(value);
      setFromError("");

      if (value.length === 5) {
        if (validateTimeFormat(convertTo24Hour(value, fromPeriod))) {
          onFromChange(convertTo24Hour(value, fromPeriod));
        } else {
          setFromError("Invalid time format");
        }
      }
    } else {
      setToTimeInput(value);
      setToError("");

      if (value.length === 5) {
        if (validateTimeFormat(convertTo24Hour(value, toPeriod))) {
          onToChange(convertTo24Hour(value, toPeriod));
        } else {
          setToError("Invalid time format");
        }
      }
    }
  };

  const handlePeriodChange = (period, type) => {
    if (type === "from") {
      setFromPeriod(period);
      if (fromTimeInput.length === 5) {
        onFromChange(convertTo24Hour(fromTimeInput, period));
      }
    } else {
      setToPeriod(period);
      if (toTimeInput.length === 5) {
        onToChange(convertTo24Hour(toTimeInput, period));
      }
    }
  };

  return (
    <div className="bg-secondary p-2 rounded-md">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-end gap-2">
          <div>
            <h1 className="text-[10px] text-gray-500 text-left uppercase mb-1">
              From
            </h1>
            <div
              className="flex items-center border-[.5px] border-primary rounded-sm overflow-hidden"
              data-testid="from-time-wrapper">
              <Input
                ref={fromInputRef}
                value={fromTimeInput}
                onChange={(e) => handleTimeInput(e.target.value, "from")}
                placeholder="11:45"
                maxLength={5}
                className="w-14 text-[10px] p-1 border-none focus:ring-0"
                status={fromError ? "error" : ""}
              />
              <div className="flex border-l border-primary">
                <button
                  onClick={() => handlePeriodChange("AM", "from")}
                  className={`px-2 py-1 text-[10px] transition-colors ${
                    fromPeriod === "AM"
                      ? "bg-primary text-white"
                      : "bg-transparent text-primary hover:bg-primary hover:text-white"
                  }`}>
                  AM
                </button>
                <button
                  onClick={() => handlePeriodChange("PM", "from")}
                  className={`px-2 py-1 text-[10px] transition-colors ${
                    fromPeriod === "PM"
                      ? "bg-primary text-white"
                      : "bg-transparent text-primary hover:bg-primary hover:text-white"
                  }`}>
                  PM
                </button>
              </div>
            </div>
            {fromError && (
              <p className="text-[10px] text-red-500 mt-1">{fromError}</p>
            )}
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div>
            <h1 className="text-[10px] text-gray-500 text-end uppercase mb-1">
              To
            </h1>
            <div
              className="flex items-center border-[.5px] border-primary rounded-sm overflow-hidden"
              data-testid="to-time-wrapper">
              <Input
                ref={toInputRef}
                value={toTimeInput}
                onChange={(e) => handleTimeInput(e.target.value, "to")}
                placeholder="02:30"
                maxLength={5}
                className="w-14 text-[10px] p-1 border-none focus:ring-0"
                status={toError ? "error" : ""}
              />
              <div className="flex border-l border-primary">
                <button
                  onClick={() => handlePeriodChange("AM", "to")}
                  className={`px-2 py-1 text-[10px] transition-colors ${
                    toPeriod === "AM"
                      ? "bg-primary text-white"
                      : "bg-transparent text-primary hover:bg-primary hover:text-white"
                  }`}>
                  AM
                </button>
                <button
                  onClick={() => handlePeriodChange("PM", "to")}
                  className={`px-2 py-1 text-[10px] transition-colors ${
                    toPeriod === "PM"
                      ? "bg-primary text-white"
                      : "bg-transparent text-primary hover:bg-primary hover:text-white"
                  }`}>
                  PM
                </button>
              </div>
            </div>
            {toError && (
              <p className="text-[10px] text-red-500 mt-1">{toError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

TimeRangeInput.propTypes = {
  fromTime: PropTypes.string,
  toTime: PropTypes.string,
  onFromChange: PropTypes.func,
  onToChange: PropTypes.func,
  className: PropTypes.string,
};

export default TimeRangeInput;
